import { BreakpointTrackerContext } from '@jutro/layout';
import { useAuthentication } from 'gw-digital-auth-react';
import { LoadSaveService } from 'gw-capability-gateway-quoteandbind';
import React, { useCallback, useContext, useState, useEffect } from 'react';
import _ from 'lodash';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import { InputField, CheckboxField, DropdownSelectField, ModalNextProvider } from '@jutro/components';
import { TranslatorContext } from '@jutro/locale';
import {ViewModelServiceContext} from 'gw-portals-viewmodel-react'
import { WizardPage, wizardProps } from 'gw-portals-wizard-react';
import { useValidation } from 'gw-portals-validation-react';

import metadata from './CommonPackageDetailsPage.metadata.json5';
import messages from './CommonPackageDetailsPage.messages';
import styles from './CommonPackageDetailsPage.module.scss';
import { messages as commonMessages } from 'gw-platform-translations';
import { useDependencies } from 'gw-portals-dependency-react';

function CommonPackageDetailsPage(props) {
    const translator = useContext(TranslatorContext);
    const { wizardData: submissionVM, updateWizardData, additionalWizardData, updateAdditionalWizardData} = props;
    const viewModelService = useContext(ViewModelServiceContext);
    const { SpreadsheetService } = useDependencies('SpreadsheetService');
    const [isPageInitialized, setPageInitialized] = useState(false);
    const { DocumentService } = useDependencies('DocumentService');
    const { isComponentValid, initialValidation, onValidate,registerComponentValidation } = useValidation('CommonPackageDetailsPage');
    const { authHeader } = useAuthentication();
    const [showLoader, setShowLoader] = useState(false);
    const [underwritingIssues, setUnderwritingIssues] = useState([]);
    const [errorsAndWarnings, setErrorsAndWarnings] = useState([]);
    const [excelErrorPresent, setExcelErrorPresent] = useState(false);
    const filterUWIssuesInCustomOffering = true;

    const BLOCKS_QUOTES = ['BlocksQuote', 'BlocksQuoteRelease'];
    const BLOCKS_BIND = 'BlocksBind';
    const NON_BLOCKING = 'NonBlocking';

    const displayErrorModal = useCallback(() => {
        settemplateDownloaded(false);
        ModalNextProvider.showAlert({
            title: messages.error,
            message: messages.errorMessage,
            status: 'error',
            icon: 'mi-error-outline',
            confirmButtonText: commonMessages.ok
        }).catch(_.noop);
    }, []);

    const isDetailsComponentValid = useCallback(() => {
        return true;
    }, [submissionVM]);

    useEffect(() => {
        setPageInitialized(true);
        registerComponentValidation(isDetailsComponentValid);
        // The above action only need to run once when the page is mounted
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [registerComponentValidation, isDetailsComponentValid]);

    const onValueChange = useCallback((value, item, prop) => {
        const index = submissionVM.lobData.homeowners.allCoverables.value.indexOf(item.value);
        const path = `lobData.homeowners.allCoverables.children.${index}.${prop.dataPath}`;
        const newSubmissionVM = viewModelService.clone(submissionVM);
        _.set(newSubmissionVM, path, value);
        if(prop.dataPath === 'isPrimary') {
            newSubmissionVM.lobData.homeowners.allCoverables.children.forEach(element => {
                if(element._accessorCode != item._accessorCode) {
                    _.set(element, 'isPrimary', false);
                }
            });
        }
        updateWizardData(newSubmissionVM);
        }, [submissionVM]
    );

    const handleError = useCallback(
        (title, message) => {
            return ModalNextProvider.showAlert({
                title: title,
                message: message,
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: commonMessages.ok
            }).catch(_.noop);
        }, []
    );

    const generateUploadToken = useCallback(async () => {
        const uploadTokenID = await DocumentService.generateUploadToken(authHeader);
        return uploadTokenID;
    }, [DocumentService, authHeader]);

    const onUploadDocument = useCallback(
        async (file) => {
            setShowLoader(true);
            setExcelErrorPresent(false);
            const documentMetaDataTemplate = {
                docUID: '001',
                documentType: 'other',
                securityType: 'unrestricted',
                status: 'approved',
                jobNumber: submissionVM.quoteID.value,
                name: file.name,
                mimeType: file.type,
                sessionID: await generateUploadToken()
            };
            try {
                const data = await SpreadsheetService.uploadDocument(
                    file,
                    documentMetaDataTemplate,
                    authHeader
                );
                if(data.quoteData && data.quoteData.errorsAndWarnings && data.quoteData.errorsAndWarnings.underwritingIssues){
                    setUnderwritingIssues(data.quoteData.errorsAndWarnings.underwritingIssues);
                }
                if(data.quoteData && data.quoteData.errorsAndWarnings && data.quoteData.errorsAndWarnings.validationIssues && data.quoteData.errorsAndWarnings.validationIssues.issues){
                    setErrorsAndWarnings(data.quoteData.errorsAndWarnings.validationIssues.issues);
                }
                const requestData = {
                    quoteID: submissionVM.quoteID.value,
                    postalCode: submissionVM.baseData.account.accountHolder.primaryAddress.postalCode.value
                };
                submissionVM.value = await LoadSaveService.retrieveSubmission(
                    requestData,
                    authHeader
                );
                if(submissionVM.baseData.periodStatus.value.code === 'Quoted') {
                    updateAdditionalWizardData({isFarmLineQuoted: true})
                }
                setShowLoader(false);
                if(data.fileBase64Data){
                    setExcelErrorPresent(true);
                    var base64EncodedPDF = data.fileBase64Data;
                    var dataURI = "data:"+data.documentFile.contentType+";base64," + base64EncodedPDF;
                    //window.open(dataURI, '_blank');
                    var link = document.createElement("a");
                    link.href = dataURI;
                    //link.download = "FarmQuote_"+submissionVM.quoteID.value;
                    if(submissionVM.baseData.productCode.value == "BP7BusinessOwners"){
                        link.download = "FarmQuote_"+submissionVM.quoteID.value;
                    }else{
                        link.download = "CPQuote_"+submissionVM.quoteID.value;
                    }
                    link.click();
                } else {
                    setExcelErrorPresent(false);
                    ModalNextProvider.showAlert({
                        title: messages.successMessage,
                        message: messages.successMessageInfo,
                        status: 'info',
                        icon: 'mi-error-outline',
                        confirmButtonText: commonMessages.ok
                    }).catch(_.noop);
                }
            } catch (error) {
                console.log(error);
                setShowLoader(false);
                handleError(commonMessages.errorUploadTitle, commonMessages.uploadFailedMessage);
            }
        },
        [submissionVM, generateUploadToken, SpreadsheetService, authHeader, updateWizardData, handleError, setExcelErrorPresent]
    );

    const downloadQuoteDocument = useCallback(async () => {
            setShowLoader('download');
            const data = await SpreadsheetService
                .retrieveBlankTemplate(submissionVM.quoteID.value, authHeader, displayErrorModal);
            setShowLoader(false);
            if(data.fileBase64Data){
                var base64EncodedPDF = data.fileBase64Data;
                const contentType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                var dataURI = "data:" + contentType + ";base64," + base64EncodedPDF;
                var link = document.createElement("a");
                link.href = dataURI;              
                if(submissionVM.baseData.productCode.value == "BP7BusinessOwners"){
                    link.download = "FarmQuote_"+submissionVM.quoteID.value;
                }else{
                    link.download = "CPQuote_"+submissionVM.quoteID.value;
                }
                link.click();
            }
        },
        [submissionVM, authHeader, displayErrorModal, SpreadsheetService]
    );

    const handleDownloadTemplate = useCallback(() => {
        setShowLoader(true);
        downloadQuoteDocument();
    }, [downloadQuoteDocument]);

    const geterrorsAndWarningsTableCell = useCallback(
        (item, index, props) => {
            var path = props.path;
            if (path == 'errorType') {
                const errorType = item.type === 'error' ? 'Error' : 'Warning'
                return (
                    <InputField
                        dataType="string"
                        id={index + path}
                        value={errorType}
                        readOnly={true}
                    />
                )
            } else if (path == 'displayName') {
                return (
                    <InputField
                        dataType="string"
                        id={index + path}
                        value={item.reason}
                        readOnly={true}
                    />
                )
            }
        },
        []
    );

    const findBlockingPoint = (type) => {
        return underwritingIssues.some((offeringIssue) => {
            if (type === BLOCKS_BIND) {
                return offeringIssue.currentBlockingPoint === BLOCKS_BIND;
            }
            return _.includes(BLOCKS_QUOTES, offeringIssue.currentBlockingPoint);
        });
    };

    const overrideProps = {
        '@field': {
            labelPosition: 'left'
        },
        /*underWritingIssues: {
            underwritingIssues: underwritingIssues,
            blockQuote: findBlockingPoint(),
            quoteID: submissionVM.quoteID.value,
            filterUWIssuesInCustomOffering
        },
        errorsAndWarningsTable: {
            data: errorsAndWarnings
        },
        errorsAndWarningsTableContainer: {
            visible: errorsAndWarnings.length > 0
        },*/
        excelErrorMessage: {
            visible: excelErrorPresent
        },
        processInlineLoader: {
            loaded: !showLoader,
            text: showLoader == 'download' ? translator(messages.downloadWaitMessage) : translator(messages.uploadWaitMessage)
        },
        PackageDetailsUploadPage: {
            visible: !showLoader
        },
    };

    const onNext = useCallback(async () => {
        if(additionalWizardData.isFarmLineQuoted && submissionVM.baseData.periodStatus.value.code !== 'Quoted'){
            _.unset(submissionVM.value, 'bindData');
            submissionVM.value = await LoadSaveService.saveAndQuoteSubmission(
                submissionVM.value,
                authHeader
            );
        }
        return submissionVM;
    }, [submissionVM, LoadSaveService, authHeader, additionalWizardData]);

    const isInDraft = () => {
        return submissionVM.baseData.periodStatus.value.code !== 'Quoted' && additionalWizardData.isFarmLineQuoted !== true;
    };

    const resolvers = {
        resolveCallbackMap: {
            onUploadDocument: onUploadDocument,
            geterrorsAndWarningsTableCell: geterrorsAndWarningsTableCell,
            downloadQuoteDocument: downloadQuoteDocument
        }
    };

    if (!isPageInitialized) {
        return null;
    }

    const skipInitialValidation = () => {
        const periodStatus = _.get(submissionVM.value, 'baseData.periodStatus');
        const quoted = periodStatus === 'Quoted';
        return quoted && submissionVM && submissionVM.aspects.valid && submissionVM.aspects.subtreeValid;
    }

    return (
        <WizardPage onNext={onNext} disableNext={isInDraft()} skipWhen={skipInitialValidation}>
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={submissionVM}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                onValidationChange={onValidate}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={styles}
            />
        </WizardPage>
    );
}

CommonPackageDetailsPage.propTypes = wizardProps;
export default CommonPackageDetailsPage;
